import { logger } from '@adornis/base/logging.js';
import type { Maybe } from '@adornis/base/utilTypes.js';
import { Entity, Field } from '@adornis/baseql/decorators.js';
import { AdornisEntity } from '@adornis/baseql/entities/adornisEntity.js';
import type { EntityData, PartialEntityData } from '@adornis/baseql/entities/types.js';
import { registerQuery } from '@adornis/baseql/metadata/register.js';
import { getCollection, getRawCollection } from '@adornis/baseql/server/collections.js';
import type { BaseQLSelectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { AdornisLLMRouter } from '@adornis/llm/server/llm-router.js';
import { cleanHtmlContent } from '@source/backup/server/formatting-and-regex.js';
import { fetchOdooThreadFollowingDraft } from '@source/odoo/general-service.js';
import { startOfMonth, subMonths } from 'date-fns';
import type { Filter } from 'mongodb';
import { AnsweringChannelType, AnsweringEntity, getOdooItemsMissingAnswersCursor } from '../entities/answering.js';
import { IntercomEntity, UserType } from '../entities/intercom.js';
import { addMessagesToOdooEntity, OdooEntity } from '../entities/odoo.js';
import { ProductDataChunkEntity } from '../entities/productdatachunk.js';
import {
  AverageRatingEntryEntity,
  getRatingByTag,
  RatingEntity,
  RatingMapEntryEntity,
  RatingSummaryEntity,
  upsertTag,
} from '../entities/rating.js';
import { checkArticleNumbers } from '../utils/article-service.js';

// TODO currentyl unused, i guess this need to run regularly as soon as we want to cehck against to actual given answer of the human assistant
// ! looks like we somehow need this because the rating calculation needs the expectedAnswers
export async function fetchOdooDraftsExpectedAnswers() {
  const responseArray: Record<string, any>[] = [];

  // get all answers with missing expectedAnswers from the last 2 months
  const answeringEntitiesMissingAnswers = await getOdooItemsMissingAnswersCursor(
    subMonths(startOfMonth(new Date()), 2),
  );

  const count = await answeringEntitiesMissingAnswers.count();
  logger.info({ answeringEntitiesMissingAnswers: count }, `Found ${count} answering entities with missing answers`);

  for await (const answeringEntity of answeringEntitiesMissingAnswers) {
    const foreignKey = answeringEntity.foreignKey;
    const conversationPartId = answeringEntity.conversationPartId;
    // if answeringEntity.foreignKey and answeringEntity.conversationPartId are not numbers, continue
    if (!foreignKey || !conversationPartId || isNaN(Number(foreignKey)) || isNaN(Number(conversationPartId))) {
      logger.warn(
        `Skipping answering entity with foreignKey: ${foreignKey}, conversationPartId: ${conversationPartId} because they are not numbers`,
      );
      continue;
    }
    const odooTicketID = Number(answeringEntity.foreignKey);
    const messageID = Number(answeringEntity.conversationPartId);
    const messages = await fetchOdooThreadFollowingDraft(odooTicketID, messageID);
    logger.info({ messages: messages?.length }, 'MESSAGES?');
    if (messages && messages.length > 0) {
      responseArray.push({
        foreignKey: foreignKey,
        conversationPartId: conversationPartId,
        messages: messages.map(msg => ({
          body: msg.body,
          odooMailMessageId: msg.id,
          emailFrom: msg.email_from,
        })),
      });
      // try adding the next message to odooEntity
      try {
        const supportAnswerMessage = {
          body: messages[0].body,
          id: messages[0].id.toString(),
          email_from: messages[0].email_from,
          create_date: messages[0].create_date,
        };
        await addMessagesToOdooEntity(foreignKey, [supportAnswerMessage]);
      } catch (err) {
        logger.error(
          { err },
          `Error adding messages to odooEntity for foreignKey: ${foreignKey}, conversationPartId: ${conversationPartId}`,
        );
      }
      // update answeringEntity with expectedAnswer
      const answeringCollection = await getCollection<AnsweringEntity>(AnsweringEntity._class);
      const updateResult = await answeringCollection.updateOne(
        {
          _id: answeringEntity._id,
        },
        {
          $set: {
            expectedAnswer: messages[0].body,
          },
        },
      );
      logger.info(
        { updateResult },
        `Updated answering entity with foreignKey: ${foreignKey}, conversationPartId: ${conversationPartId}`,
      );
    }
  }
  return responseArray;
}

export const averageRatings = registerQuery({
  operationName: 'averageRatings',
  resolve: () => async (gqlFields: BaseQLSelectionSet<RatingSummaryEntity>) => {
    const collection = await getCollection(AnsweringEntity);

    // get all items with query, answer and expectedAnswer and without rating
    const answerings = await collection
      .find(
        {
          query: { $exists: true, $ne: null },
          rating: { $exists: true },
        },
        { projection: { rating: 1, answeringChannelType: 1, odooTags: 1 } },
      )
      .toArray();
    // calculate average rating by distinct tag
    const ratingMap: Array<PartialEntityData<RatingMapEntryEntity> & { total?: number; count?: number }> = [];
    // const ratingMap: Record<
    //   string,
    //   { total: number; count: number; numberOfAbove7: number; countTotal: number; numberOfAbove5: number }
    // > = {};
    let totalCountAnswering = 0;
    let totalCountRating = 0;
    let totalCountRelevant = 0;
    let totalCountGreaterEqual5 = 0;
    let totalCountGreaterEqual7 = 0;

    let totalCountAnsweringIntercom = 0;
    let totalCountRatingIntercom = 0;
    let totalCountRelevantIntercom = 0;
    let totalCountGreaterEqual5Intercom = 0;
    let totalCountGreaterEqual7Intercom = 0;

    let totalCountAnsweringEmail = 0;
    let totalCountRatingEmail = 0;
    let totalCountRelevantEmail = 0;
    let totalCountGreaterEqual5Email = 0;
    let totalCountGreaterEqual7Email = 0;

    for (const answering of answerings) {
      const answeringIsIntercom =
        answering.answeringChannelType === AnsweringChannelType.INTERCOM_CONVERSATION ||
        answering.answeringChannelType === AnsweringChannelType.INTEGRATION_TEST_CHAT;
      totalCountAnswering += 1;
      answeringIsIntercom ? (totalCountAnsweringIntercom += 1) : (totalCountAnsweringEmail += 1);
      if (answering.rating > 0) {
        totalCountRating += answering.rating || 0;
        totalCountRelevant += 1; // count only relevant ratings
        answeringIsIntercom
          ? (totalCountRatingIntercom += answering.rating || 0)
          : (totalCountRatingEmail += answering.rating || 0);
        answeringIsIntercom ? (totalCountRelevantIntercom += 1) : (totalCountRelevantEmail += 1);
      }
      if (answering.rating >= 5) {
        totalCountGreaterEqual5 += 1; // count ratings greater or equal to 5
        answeringIsIntercom ? (totalCountGreaterEqual5Intercom += 1) : (totalCountGreaterEqual5Email += 1);
      }
      if (answering.rating >= 7) {
        totalCountGreaterEqual7 += 1; // count ratings greater or equal to 7
        answeringIsIntercom ? (totalCountGreaterEqual7Intercom += 1) : (totalCountGreaterEqual7Email += 1);
      }

      const { odooTags, rating } = answering;
      if (odooTags && Array.isArray(odooTags)) {
        for (const tag of odooTags) {
          const tagName = answeringIsIntercom ? 'INTERCOM_' + tag : 'EMAIL_' + tag; // use tag name for intercom, otherwise use tag object

          let existingObject = ratingMap.find(r => r.key === tagName);
          if (!existingObject) {
            existingObject = {
              _class: RatingMapEntryEntity._class,
              key: tagName,
              total: 0,
              count: 0,
              numberOfAbove7: 0,
              countTotal: 0,
              numberOfAbove5: 0,
            };
            ratingMap.push(existingObject);
          }
          if (rating && rating > 0) {
            existingObject.total! += rating;
            existingObject.count! += 1;
          }
          if (rating && rating >= 7) {
            existingObject.numberOfAbove7! += 1;
          }
          if (rating && rating >= 5) {
            existingObject.numberOfAbove5! += 1;
          }
          existingObject.countTotal! += 1; // total count of ratings for this tag
        }
      }
    }

    // return null;

    // calculate average rating for each tag
    const averageRatings: PartialEntityData<AverageRatingEntryEntity>[] = ratingMap.map(r => ({
      _class: AverageRatingEntryEntity._class,
      key: r.key,
      value: r.count! > 0 ? r.total! / r.count! : 0,
    }));

    // remove keys total and count from ratingMap
    for (const rating of ratingMap) {
      await upsertTag(
        rating.key!,
        averageRatings.find(r => r.key === rating.key)?.value || 0,
        '-',
        rating.countTotal!,
        rating.numberOfAbove5!,
        rating.numberOfAbove7!,
      );
      delete rating.total;
      delete rating.count;
    }

    return {
      _class: RatingSummaryEntity._class,
      totalCountAnsweringGlobal: totalCountAnswering,
      totalCountRatingGlobal: totalCountRating / totalCountRelevant,
      totalCountRelevantGlobal: totalCountRelevant,
      totalCountGreaterEqual5Global: totalCountGreaterEqual5,
      totalCountGreaterEqual7Global: totalCountGreaterEqual7,
      totalCountAnsweringIntercom: totalCountAnsweringIntercom,
      totalCountRatingIntercom: totalCountRatingIntercom / totalCountRelevantIntercom,
      totalCountRelevantIntercom: totalCountRelevantIntercom,
      totalCountGreaterEqual5Intercom: totalCountGreaterEqual5Intercom,
      totalCountGreaterEqual7Intercom: totalCountGreaterEqual7Intercom,
      totalCountAnsweringEmail: totalCountAnsweringEmail,
      totalCountRatingEmail: totalCountRatingEmail / totalCountRelevantEmail,
      totalCountRelevantEmail: totalCountRelevantEmail,
      totalCountGreaterEqual5Email: totalCountGreaterEqual5Email,
      totalCountGreaterEqual7Email: totalCountGreaterEqual7Email,
      averageRatings: averageRatings,
      totalAnswerings: answerings.length,
      totalRatings: answerings.reduce((sum, answering) => sum + (answering.rating || 0), 0),
      ratingMap: ratingMap,
    };
  },
  type: () => RatingSummaryEntity,
});

export async function ratingForAnsweringItem(foreignKey: string, conversationPartId: string) {
  const answeringCollection = await getCollection<AnsweringEntity>(AnsweringEntity._class);
  const answeringObj = await answeringCollection.findOne({
    foreignKey: foreignKey,
    conversationPartId: conversationPartId,
  });
  const { _id, query, answer, expectedAnswer, answeringChannelType } = answeringObj || {};
  // fetch all previous conversatio parts from the foreignKey and being before the conversationPartId
  let previousConversationParts = '';
  if (
    answeringChannelType === AnsweringChannelType.INTERCOM_CONVERSATION ||
    AnsweringChannelType.INTEGRATION_TEST_CHAT
  ) {
    try {
      const intercomCollection = await getCollection<IntercomEntity>(IntercomEntity._class);
      const intercomConversation = await intercomCollection
        .find({
          intercomId: foreignKey,
        })
        .toArray();
      let intercomObject = intercomConversation[0] || {};
      let intercomObjectBody = intercomObject.message || '';
      previousConversationParts += `\n\Initial client message: ${cleanHtmlContent(intercomObjectBody)}`;
      let intercomConversationMessages = intercomConversation[0]?.intercomConversationItems || [];
      intercomConversationMessages = intercomConversationMessages.filter(
        item => item.userType === UserType.USER || item.userType === UserType.ADMIN,
      );

      // sort the intercomConversationMessages by conversationItemId
      intercomConversationMessages = intercomConversationMessages
        .filter(item => item.conversationItemId < conversationPartId)
        .sort((a, b) => a.conversationItemId.localeCompare(b.conversationItemId));

      for (let intercomConversationMessage of intercomConversationMessages) {
        if (intercomConversationMessage.body) {
          previousConversationParts += `\n\n${
            intercomConversationMessage.userType === UserType.USER ? 'User' : 'Support'
          }: ${cleanHtmlContent(intercomConversationMessage.body)}`;
        }
      }
    } catch (err) {
      logger.error({ err }, 'Error fetching Intercom conversation:');
    }
  }

  if (answeringChannelType === AnsweringChannelType.INTEGRATION_TEST_EMAIL || AnsweringChannelType.MAIL) {
    try {
      const odooCollection = await getCollection<OdooEntity>(OdooEntity._class);
      const odooConversation = await odooCollection
        .find({
          odooTicketID: foreignKey,
        })
        .toArray();
      let odooConversationMessages = odooConversation[0]?.messages || [];
      odooConversationMessages = odooConversationMessages.filter(
        item =>
          (item.odooMessageType === 'CLIENT' || item.odooMessageType === 'SOURCE') &&
          item.odooMailMessageId &&
          item.odooMailMessageId < conversationPartId,
      );

      // sort the intercomConversationMessages by conversationItemId
      odooConversationMessages = odooConversationMessages
        .filter(item => item.odooMailMessageId < conversationPartId)
        .sort((a, b) => a.odooMailMessageId.localeCompare(b.odooMailMessageId));

      for (let odooConversationMessage of odooConversationMessages) {
        if (odooConversationMessage.body) {
          previousConversationParts += `\n\n${
            odooConversationMessage.odooMessageType === 'CLIENT' ? 'User' : 'Support'
          }: ${cleanHtmlContent(odooConversationMessage.body)}`;
        }
      }
    } catch (err) {
      logger.error({ err }, 'Error fetching Intercom conversation:');
    }
  }

  // if any of them is empty, continue
  if (!query || !answer || !expectedAnswer) {
    return;
  }

  if (previousConversationParts.length < 1) {
    previousConversationParts = '-';
  }

  const userMessage = `userQuestion: ${query}\n\ndraft: ${answer}\n\ncorrected/ sent answer from support: ${expectedAnswer}, PreviousConversationParts: ${previousConversationParts}\n\n`;

  const ratingResponse = await llmResponse(userMessage, RATING_PROMPT);
  let ratingResult = null;
  const ratingRecursive = ['10', '8', '5', '3', '1', '0'];
  for (const r of ratingRecursive) {
    let last5CharsOfResponse = ratingResponse?.slice(-5).trim();
    if (last5CharsOfResponse?.includes(r)) {
      ratingResult = parseInt(r, 10);
      break;
    }
  }
  if (ratingResult === null) {
    const ratingUnlisted = ['9', '7', '6', '4', '2'];
    for (const r of ratingUnlisted) {
      let last5CharsOfResponse = ratingResponse?.slice(-5).trim();
      if (last5CharsOfResponse?.includes(r)) {
        ratingResult = parseInt(r, 10);
        break;
      }
    }
  }
  if (ratingResult === null) {
    ratingResult = 1;
  }

  if (answeringChannelType === AnsweringChannelType.INTEGRATION_TEST_EMAIL || AnsweringChannelType.MAIL) {
    const odooCollection = await getCollection<OdooEntity>(OdooEntity._class);
    const odooTicket = await odooCollection.findOne({ odooTicketID: foreignKey });
    // update rating from message with conversationPartId
    if (odooTicket) {
      const messageToUpdate = odooTicket.messages.find(msg => msg.odooMailMessageId === conversationPartId);
      if (messageToUpdate) {
        messageToUpdate.rating = ratingResult;
        messageToUpdate.ratingText = ratingResponse;
        await odooCollection.updateOne({ odooTicketID: foreignKey }, { $set: { messages: odooTicket.messages } });
      }
    }
  } else {
    const intercomCollection = await getCollection<IntercomEntity>(IntercomEntity._class);
    const intercomTicket = await intercomCollection.findOne({ intercomId: foreignKey });
    // update rating from message with conversationPartId
    if (intercomTicket) {
      const messageToUpdate = intercomTicket.intercomConversationItems.find(
        msg => msg.conversationItemId === conversationPartId,
      );
      if (messageToUpdate) {
        messageToUpdate.rating = ratingResult;
        messageToUpdate.ratingText = ratingResponse;
        await intercomCollection.updateOne(
          { intercomId: foreignKey },
          { $set: { intercomConversationItems: intercomTicket.intercomConversationItems } },
        );
      }
    }
  }
  logger.info(
    `Updated rating finished with rating: ${ratingResult} for answering item with foreignKey: ${foreignKey}, conversationPartId: ${conversationPartId}`,
  );
  const answeringItems = await answeringCollection
    .find({
      foreignKey: foreignKey,
      conversationPartId: conversationPartId,
    })
    .toArray();
  if (answeringItems.length < 1) {
    logger.warn(`No answering items found for foreignKey: ${foreignKey}, conversationPartId: ${conversationPartId}`);
    return;
  }
  for (const answeringItem of answeringItems) {
    if (answeringItem._id) {
      // update rating and ratingText in answering item
      await answeringCollection.updateOne(
        { _id: answeringItem._id },
        { $set: { rating: ratingResult, ratingText: ratingResponse } },
      );
      logger.info(
        `Updated answering item with _id: ${answeringItem._id} with rating: ${ratingResult} and ratingText: ${ratingResponse}`,
      );
    } else {
      logger.warn(
        `No _id found for answering item with foreignKey: ${foreignKey}, conversationPartId: ${conversationPartId}`,
      );
    }
  }
  return;
}

// ? helper to rate all answerings, do we want to make this available somehome?
export async function rateAnswerings(usecase?: 'intercom' | 'odoo'): Promise<void> {
  const collection = await getCollection<AnsweringEntity>(AnsweringEntity._class);

  const conditionObj: Filter<EntityData<AnsweringEntity>> = {
    $and: [
      {
        $or: [{ rating: { $exists: false } }, { rating: null }, { rating: undefined }],
      },
      {
        $and: [
          { answer: { $exists: true } },
          { answer: { $ne: null } },
          { answer: { $ne: '' } },
          { expectedAnswer: { $exists: true } },
          { expectedAnswer: { $ne: null } },
          { expectedAnswer: { $ne: '' } },
        ],
      },
    ],
  };

  if (usecase && usecase === 'intercom') {
    conditionObj.answeringChannelType = AnsweringChannelType.INTERCOM_CONVERSATION;
  } else if (usecase && usecase === 'odoo') {
    conditionObj.answeringChannelType = AnsweringChannelType.MAIL;
  }

  // get all items with query, answer and expectedAnswer and without rating
  const answerings = await collection.find(conditionObj);
  console.log(`Found ${await answerings.count()} answerings to rate.`);

  for await (const answer of answerings) {
    if (!(answer.query && answer.answer && answer.expectedAnswer)) continue;

    await ratingForAnsweringItem(answer.foreignKey!, answer.conversationPartId!);
  }
}

export const llmResponse = async (userMessage: string, systemPrompt: string) => {
  const llmRouter = new AdornisLLMRouter({
    provider: 'openrouter',
    model: 'google/gemini-3.1-pro-preview',
    // max_tokens: 4000,
  });
  const response = await llmRouter.chat.completions.create({
    messages: [
      { role: 'system', content: systemPrompt },
      { role: 'user', content: userMessage },
    ],
  });
  return response.choices[0]?.message.content;
};

export const RATING_PROMPT = `you are an expert analyzing response drafts given by a bot in a conversation and comparing them with expected answer provided by human support.

You have to rate the draft response based on how well it matches the expected answer and how well it answers the customer question.

Score 1: The new response is completely wrong, gives contrary or completely irrelevant information
Score 3: The new response has minor relevance but does not align with the sent email
Score 5: The new response has moderate relevance but contains significant differences
Score 8: The new response aligns with the sent email, but has minor information not present in sent
Score 10: New response information is included in  sent email, responses are largely the same in content
Score 0: The new response is not related to the question asked by the user or could not have been given by an employee that has full process handbook, e.g. the user  asks about a product and the support answers Bob is going to phone them soon.
Only whole numbers between 0 and 10 are allowed.


Rules:
- ignore personalized greeting and signature differences
- ignore not informative sentences like "thank you for your email" or "if you have more questions let me know"
- focus only on content differences
- ignore details differences in sent email if not asked in user email, don't penalize
- if in draft one answer is correct and other is wrong, give average rating
- having placeholder [] in draft is not wrong, but if minus one point
- exclude from differences the extra details in new response or sent email if they were not asked in original email from custumer
- exclude the difference in price, if the sent email has indicated a price but the draft says an offer/quotation has been attached
- ignore detials in the sent that are not asked, more information links, just core information
- ignore recommendation that are not asked in user email
- attaching the quote instead of saying the price is ok
- ignore anything related to persons, like "Bob will call you" or "I will forward this to Bob" or "Bob will send you the quote"
- ignore messages about the chatwindow, like "I will close the chat now" or "I will close the ticket now"
- ignore any polite phrases like "thank you for your email" or "if you have more questions let me know" - e.g. consider "you are welcome" and "great, come back to us if you have more questions" as the same

format answer:

english user question short:

english new response answer short:

english sent answer core info short: [use same words as in draft if exists]

english differences between sent and draft short:
[leave empty if new answer is included in sent]

rating: [[number here]]

-----
make sure the rating is the last char and there is no other char after this.

`;

export const getRatingsByTagList = registerQuery({
  operationName: 'getRatingsByTagList',
  type: () => [RatingEntity],
  params: [{ name: 'tags', type: () => [String] }],
  resolve: (tags: string[]) => async (gqlFields: BaseQLSelectionSet<RatingEntity>) => {
    if (!tags?.length) return [];

    const ratings: RatingEntity[] = [];
    try {
      for (const tag of tags) {
        const rating = await getRatingByTag(tag);
        if (rating) {
          ratings.push(rating);
        }
      }

      logger.info(`Fetched following ratings for tags: ${tags.join(', ')} - ${JSON.stringify(ratings)}`);
      return ratings;
    } catch (error) {
      console.error('Error fetching rating by tag:', error);
      throw error;
    }
  },
});

@Entity()
export class LinkAndArticleName extends AdornisEntity {
  static override _class = 'LinkAndArticleName';

  @Field(() => [ProductDataChunkEntity])
  correctProducts: Maybe<ProductDataChunkEntity[]>;

  @Field(() => [String])
  incorrectProducts: Maybe<string[]>;
}

export const checkLinkAndArticleName = registerQuery({
  operationName: 'checkLinkAndArticleName',
  type: () => LinkAndArticleName,
  params: [{ name: 'answer', type: () => String }],
  resolve: (answer: string) => async (gqlFields: BaseQLSelectionSet<LinkAndArticleName>) => {
    const productList: Record<string, any>[] = [];
    const matchedSubstrings: string[] = [];

    try {
      if (!answer) {
        return null;
      }
      logger.info(`Checking links and article names in answer`);
      const productDataChunks = await getRawCollection<ProductDataChunkEntity>(ProductDataChunkEntity._collectionName);
      let productResults = checkArticleNumbers(answer);
      // check for links in query with regex
      const linkRegex = /https?:\/\/[^\s]+/g;
      const rawLinks = answer.match(linkRegex) || [];

      // Clean trailing punctuation like ), ], ., etc.
      const links = rawLinks.map(link => link.replace(/[)\]\}>"'`.,;!?]+$/, ''));
      if ((!links || links.length === 0) && productResults.length === 0) {
        return null;
      }
      logger.info(`Found links: ${links ? links.join(', ') : 'None'}, article numbers: ${productResults.join(', ')}`);
      if (links && links.length > 0) {
        for (const link of links) {
          const product = await productDataChunks.find({ value: { $regex: link, $options: 'i' } }).toArray();
          if (product && product.length > 0) {
            productList.push(...product);
          } else {
            matchedSubstrings.push(link);
          }
        }
      }
      if (productResults.length > 0) {
        for (const productResult of productResults) {
          const product = await productDataChunks.find({ article_nr: productResult }).toArray();
          if (product && product.length > 0) {
            productList.push(...product);
          } else {
            matchedSubstrings.push(productResult);
          }
        }
      }
      return new LinkAndArticleName({
        correctProducts: productList,
        incorrectProducts: matchedSubstrings,
      });
    } catch (error) {
      logger.error({ err: error }, 'Error checking links and article names');
      throw new Error('Error checking links and article names');
    }
  },
});
